import {
  LANGUAGE_CONFIG,
  DEFAULT_LANGUAGE,
  AVAILABLE_LANGUAGES
} from "config/language-config";

// ============================================================================
// Types
// ============================================================================

/** Dynamic language type derived from LANGUAGE_CONFIG */
export type Language = keyof typeof LANGUAGE_CONFIG;

/** SEO content structure for a single page */
export interface SEOContent {
  title: string;
  description: string;
  keywords: string;
}

/** Multilingual SEO content - partial record with at least 'en' required */
export type MultilingualSEO = Partial<Record<Language, SEOContent>> & {
  en: SEOContent; // English is always required as fallback
};

/** Multilingual string content */
export type MultilingualString = Partial<Record<Language, string>> & {
  en: string; // English is always required as fallback
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get localized string with fallback to English
 */
export function getLocalizedString(
  content: MultilingualString,
  lang: string
): string {
  const language = lang as Language;
  return content[language] ?? content[DEFAULT_LANGUAGE as Language] ?? content.en;
}

/**
 * Get localized SEO content with fallback to English
 */
export function getLocalizedSEO(
  content: MultilingualSEO,
  lang: string
): SEOContent {
  const language = lang as Language;
  return content[language] ?? content[DEFAULT_LANGUAGE as Language] ?? content.en;
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(lang: string): lang is Language {
  return lang in LANGUAGE_CONFIG;
}

/**
 * Get the effective language (returns default if not supported)
 */
export function getEffectiveLanguage(lang: string): Language {
  return isLanguageSupported(lang) ? lang : (DEFAULT_LANGUAGE as Language);
}

// ============================================================================
// Site Configuration
// ============================================================================

export const seoConfig = {
  siteName: {
    en: "ATF Altecflex",
    th: "เอทีเอฟ​ อัลเทกเฟล็กซ์​",
    ms: "ATF Altecflex",
    zh: "ATF Altecflex",
    "zh-TW": "ATF Altecflex",
    "zh-HK": "ATF Altecflex",
    ja: "ATF Altecflex",
    "en-US": "ATF Altecflex",
    "en-SG": "ATF Altecflex",
  } as MultilingualString,

  siteUrl: "https://www.altecflex.com",
  defaultImage: "/images/atf-og-image.jpg",

  company: {
    name: {
      en: "Altecflex Sdn Bhd",
      th: "บริษัท​ เอทีเอฟ​ อัลเทกเฟล็กซ์​ จำกัด",
      ms: "Altecflex Sdn Bhd",
      zh: "Altecflex 私人有限公司",
      ja: "Altecflex Sdn Bhd",
    } as MultilingualString,
    legalName: "Altecflex Sdn Bhd",
    foundedYear: 2018,
    logo: "https://www.altecflex.com/logo.png",
    email: "admin@altecflex.com",
    location: "Puchong, Selangor, Malaysia",
  },

  social: {
    linkedin: "https://www.linkedin.com/company/altecflex",
  },
};

// ============================================================================
// Page SEO Configuration
// ============================================================================

export const pageSEO = {
  home: {
    en: {
      title: "ATF Altecflex - Digital Transformation & Technology Solutions",
      description:
        "Leading digital transformation service provider specializing in FinTech, banking, and insurance solutions. Expert in React development, full-stack solutions, and business transformation strategies across Malaysia and globally.",
      keywords:
        "digital transformation, fintech solutions, banking technology, insurance technology, React development, full-stack development, business transformation, IT consulting Malaysia, software development, digital innovation",
    },
    th: {
      title: "ATF Altecflex - บริการดิจิทัลทรานส์ฟอร์เมชันและโซลูชันเทคโนโลยี",
      description:
        "ผู้ให้บริการด้านดิจิทัลทรานส์ฟอร์เมชันชั้นนำ เชี่ยวชาญด้าน FinTech โซลูชันธนาคารและประกันภัย ผู้เชี่ยวชาญด้าน React พัฒนา full-stack และกลยุทธ์การเปลี่ยนแปลงธุรกิจในมาเลเซียและทั่วโลก",
      keywords:
        "ดิจิทัลทรานส์ฟอร์เมชัน, โซลูชั่นฟินเทค, เทคโนโลยีธนาคาร, เทคโนโลยีประกัน, พัฒนา React, พัฒนา full-stack, การเปลี่ยนแปลงธุรกิจ, IT consulting มาเลเซีย, พัฒนาซอฟต์แวร์, นวัตกรรมดิจิทัล",
    },
    ms: {
      title: "ATF Altecflex - Transformasi Digital & Penyelesaian Teknologi",
      description:
        "Penyedia perkhidmatan transformasi digital terkemuka yang pakar dalam penyelesaian FinTech, perbankan dan insurans. Pakar dalam pembangunan React, penyelesaian full-stack, dan strategi transformasi perniagaan di Malaysia dan global.",
      keywords:
        "transformasi digital, penyelesaian fintech, teknologi perbankan, teknologi insurans, pembangunan React, pembangunan full-stack, transformasi perniagaan, perundingan IT Malaysia, pembangunan perisian, inovasi digital",
    },
    zh: {
      title: "ATF Altecflex - 数字化转型与技术解决方案",
      description:
        "领先的数字化转型服务提供商，专注于金融科技、银行和保险解决方案。在React开发、全栈解决方案和业务转型策略方面具有专业知识，服务马来西亚及全球客户。",
      keywords:
        "数字化转型, 金融科技解决方案, 银行技术, 保险技术, React开发, 全栈开发, 业务转型, IT咨询马来西亚, 软件开发, 数字创新",
    },
    ja: {
      title: "ATF Altecflex - デジタルトランスフォーメーション＆テクノロジーソリューション",
      description:
        "FinTech、銀行、保険ソリューションを専門とする大手デジタルトランスフォーメーションサービスプロバイダー。React開発、フルスタックソリューション、マレーシアおよびグローバルでのビジネス変革戦略のエキスパート。",
      keywords:
        "デジタルトランスフォーメーション, フィンテックソリューション, 銀行技術, 保険技術, React開発, フルスタック開発, ビジネス変革, ITコンサルティングマレーシア, ソフトウェア開発, デジタルイノベーション",
    },
  } as MultilingualSEO,

  productListing: {
    en: {
      title: "Our Solutions - Digital Services & Products | ATF Altecflex",
      description:
        "Explore our comprehensive digital transformation solutions including core banking systems, digital banking, mobile applications, API integrations, and custom software development for insurance and financial services.",
      keywords:
        "digital banking solutions, core banking system, mobile app development, API integration, insurance technology, financial software, custom development, agile delivery, MVP development, digital products",
    },
    th: {
      title: "โซลูชันของเรา - บริการและผลิตภัณฑ์ดิจิทัล | ATF Altecflex",
      description:
        "สำรวจโซลูชันดิจิทัลทรานส์ฟอร์เมชันที่ครอบคลุม รวมถึงระบบธนาคารหลัก ดิจิทัลแบงก์กิ้ง แอปพลิเคชันมือถือ การรวมระบบ API และการพัฒนาซอฟต์แวร์สำหรับประกันภัยและบริการทางการเงิน",
      keywords:
        "โซลูชันดิจิทัลแบงก์กิ้ง, ระบบธนาคารหลัก, พัฒนาแอปมือถือ, API integration, เทคโนโลยีประกัน, ซอฟต์แวร์การเงิน, custom development, agile delivery, พัฒนา MVP, ผลิตภัณฑ์ดิจิทัล",
    },
    ms: {
      title: "Penyelesaian Kami - Perkhidmatan & Produk Digital | ATF Altecflex",
      description:
        "Terokai penyelesaian transformasi digital komprehensif kami termasuk sistem perbankan teras, perbankan digital, aplikasi mudah alih, integrasi API, dan pembangunan perisian tersuai untuk insurans dan perkhidmatan kewangan.",
      keywords:
        "penyelesaian perbankan digital, sistem perbankan teras, pembangunan aplikasi mudah alih, integrasi API, teknologi insurans, perisian kewangan, pembangunan tersuai, penghantaran agile, pembangunan MVP, produk digital",
    },
  } as MultilingualSEO,
};

// ============================================================================
// Convenience Getters
// ============================================================================

/**
 * Get site name for a specific language
 */
export function getSiteName(lang: string): string {
  return getLocalizedString(seoConfig.siteName, lang);
}

/**
 * Get company name for a specific language
 */
export function getCompanyName(lang: string): string {
  return getLocalizedString(seoConfig.company.name, lang);
}

/**
 * Get page SEO for a specific page and language
 */
export function getPageSEO(
  page: keyof typeof pageSEO,
  lang: string
): SEOContent {
  return getLocalizedSEO(pageSEO[page], lang);
}

/**
 * Generate full page title with site name
 */
export function getFullPageTitle(pageTitle: string, lang: string): string {
  const siteName = getSiteName(lang);
  return `${pageTitle} | ${siteName}`;
}

// ============================================================================
// Export available languages for external use
// ============================================================================

export { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_CONFIG };
